import * as types from './action-types';
import api from '../utils/api';
import getAuth from '../utils/get-auth';

export const requestRoomsList = () => {
  return (dispatch, getState) => {
    const auth = getAuth(getState());
    dispatch({
      type: types.ROOMS_REQUEST_LIST,
      payload: api(`/rooms`, {
        headers: { 'Authorization': `JWT ${auth}` }
      })
    });
  }
};

/**
 * Action for creating a new room, and optionally inviting a given list
 * of users.
 *
 * @param {Object} room The room object to create.
 * @param {String} room.name The name of the room to create.
 * @param {String[]} room.members An array of user IDs to invite.
 * @param {String} room.build The optional non-default build to use for the room.
 */
export const createRoom = ({ name, members = [], build })  => {
  return (dispatch, getState) => {
    const auth = getAuth(getState());

    let payload = { name, build };
    if (members.length) {
      payload.members = members;
    }

    dispatch({
      type: types.ROOMS_CREATE_ROOM,
      payload: api(`/rooms`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `JWT ${auth}`
        },
        body: JSON.stringify(payload)
      })
    });
  }
};

/**
 * Join the given room. The value this sets is read from the VR app
 * to determine which room it should join.
 *
 * @param {Object} room The room model given from the API.
 */
export const joinRoom = (room) => {
  if (!window.require) {
    alert("Sorry, you need to run this from Normal Chat to launch the VR app.");
    return;
  }

  // Otherwise, assume we're in the Electron app and attempt to launch it.
  const { ipcRenderer } = window.require('electron');
  ipcRenderer.send('join-room', room);
  ipcRenderer.send('start-vr-app', room);

  return { type: types.ROOMS_JOIN_ROOM, room };
};

export const clearCreatedRoom = () => {
  return { type: types.ROOMS_CLEAR_CREATED_ROOM };
};

/**
 * Update a room with the given new properties.
 *
 * @param {String} id The ID of the room to update.
 * @param {Object} props The properties to update the room with.
 *
 */
export const updateRoom = (id, props) => {
  return (dispatch, getState) => {
    const auth = getAuth(getState());
    dispatch({
      type: types.ROOMS_UPDATE_ROOM,
      payload: api(`/rooms/${id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `JWT ${auth}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(props)
      })
    });
  };
}

/**
 * Delete (or leave) the room with the given ID.
 *
 * @param {String} id The ID for the room.
 */
export const deleteRoom = (id) => {
  return (dispatch, getState) => {
    const auth = getAuth(getState());
    dispatch({
      type: types.ROOMS_DELETE_ROOM,
      payload: api(`/rooms/${id}/delete`, {
        method: 'POST',
        headers: {
          'Authorization': `JWT ${auth}`
        }
      }),
      meta: {
        room: id
      }
    });
  }
}

/**
 * Invite the given user IDs to a given room.
 *
 * @param {String} room The ID of the room to invite the users to.
 * @param {Array(String)} users The array of user IDs to invite.
 */
export const inviteUsersToRoom = (room, users) => {
  return (dispatch, getState) => {
    const auth = getAuth(getState());

    let payload = {
      members: users
    };

    dispatch({
      type: types.ROOMS_INVITE_USERS,
      payload: api(`/rooms/${room}/invite`, {
        method: 'POST',
        headers: {
          'Authorization': `JWT ${auth}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      })
    });
  }
}

/**
 * Clear out the invite users state. Called when dismissing the invite
 * flow for inviting users to an existing room.
 */
export const clearInviteUsersToRoom = () => {
  return {
    type: types.ROOMS_CLEAR_INVITE_USERS
  };
};

/**
 * Remove users from the given room.
 * @param {String} id The ID of the room to remove users from.
 * @param {Array[String]} users An array of member IDs to block from the room.
 */
export const removeUserFromRoom = (id = "", users = []) => {
  return (dispatch, getState) => {
    const auth = getAuth(getState());

    let payload = {
      members: users
    };

    dispatch({
      type: types.ROOMS_REQUEST_REMOVE_MEMBERS,
      meta: { users },
      payload: api(`/rooms/${id}/block`, {
        method: 'POST',
        headers: {
          'Authorization': `JWT ${auth}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      })
    });
  }
}



// WEBPACK FOOTER //
// ./src/actions/rooms-actions.js